<!-- This component can be used like this: -->
<!--   <sf-ace-editor v-model="textField"> -->
<!-- where textField is the liquid/kramdown text that will be edited -->
<!-- It can take an optional `availableActions` props, like this: -->
<!--   <sf-ace-editor v-model="textField" availableActions="['makeBold', 'makeItalic']"> -->
<!-- All possible actions are defined in the ALL_AVAILABLE_ACTIONS constante -->
<!-- By default, if no props is passed, all actions are available -->

<template>
  <div ref="editorContainer" class="sf-ace-editor-vue sf-ace-editor">
    <div
      v-if="supportsDiff"
      class="sf-ace-editor-diff"
      data-behaviour="sf-ace-editor-diff"
    />
    <div class="sf-ace-editor-toolbar">
      <a
        v-for="(actionData, index) in actionsData"
        :key="index"
        data-behaviour="sf-ace-editor-action"
        :data-action="actionData.action"
        :title="actionData.title"
        rel="tooltip"
        data-placement="top"
      >
        <i :class="actionData.icon" />
      </a>

      <a
        href="https://developer.silverfin.com/"
        target="_blank"
        class="sf-ace-editor-help-icon"
        title="Open documentation in new tab"
        rel="tooltip"
        data-placement="top"
        ><i class="fa fa-question"
      /></a>
    </div>
    <textarea
      ref="editorTextarea"
      type="text"
      class="sf-ace-editor-textarea"
      data-autoresize="true"
      :data-verify-url="verifyUrl"
      :data-diff-url="diffUrl"
      :value="modelValue"
    />
  </div>
</template>

<script>
import SfAceEditor from '@app/application/sf-ace/sf-ace-editor'

const ALL_AVAILABLE_ACTIONS = [
  'makeBold',
  'makeItalic',
  'makeUnderlined',
  'insertUnorderedList',
  'insertOrderedList',
  'insertTable',
  'showDiff',
  'verify',
]

const ACTIONS_DATA = [
  { action: 'makeBold', title: 'Bold', icon: 'fa fa-bold' },
  { action: 'makeItalic', title: 'Italic', icon: 'fa fa-italic' },
  { action: 'makeUnderlined', title: 'Underline', icon: 'fa fa-underline' },
  { action: 'insertUnorderedList', title: 'List', icon: 'fa fa-list-ul' },
  { action: 'insertOrderedList', title: 'Ordered list', icon: 'fa fa-list-ol' },
  { action: 'insertTable', title: 'Table', icon: 'fa fa-table' },
  { action: 'showDiff', title: 'Diff', icon: 'fa fa-eye-slash' },
  { action: 'verify', title: 'Verify', icon: 'fa fa-bug' },
]

export default {
  name: 'SfAceEditor',
  props: {
    modelValue: {
      type: String,
      required: true,
    },
    availableActions: {
      type: Array,
      required: false,
      default() {
        return ALL_AVAILABLE_ACTIONS
      },
    },
  },
  computed: {
    actionsData() {
      return ACTIONS_DATA.filter((data) =>
        this.availableActions.includes(data.action)
      )
    },
    supportsVerify() {
      return this.availableActions.includes('verify')
    },
    supportsDiff() {
      return this.availableActions.includes('showDiff')
    },
    verifyUrl() {
      return this.supportsVerify ? Silverfin.Routes.verify_liquid_path() : ''
    },
    diffUrl() {
      return this.supportsDiff ? Silverfin.Routes.diff_liquid_path() : ''
    },
  },
  mounted() {
    // ACE is not playing well with Vue.js
    // the textarea item is used to communicate between ACE and Vue.
    // Here we're initializing the value of the textarea once for making it available to ACE.
    // We can't do it in the template directly because Vue would take control of the value
    // and disallow ACE from sending updates to it.

    this.$refs.editorTextarea.onchange = this.updateValue
    new SfAceEditor(this.$refs.editorContainer)
  },
  methods: {
    updateValue(event) {
      // props named `value` + this $emit function allows to pass a v-model to
      // the component, since that's how it behaves in practice.
      this.$emit('update:modelValue', event.target.value)
    },
  },
}
</script>
